import {createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react'
import {getAllUserInformation} from '../api/common/layout/notification-push/api'
import {getAllProducts} from '../api/common/customer/api'
import {getCountNotificationByEmployeeId} from '../api/common/layout/notification'
import {useSelector} from 'react-redux'
import {getMessaging, onMessage} from 'firebase/messaging'
import {firebaseConfig, notificationCategory} from '../common/config-variable/notification'
import {initializeApp} from 'firebase/app'
import {debounce} from 'lodash/function'
import {loadEmployeeReferencesApi} from '../api/common/employee-reference/api'
import {getDepartments, getJobTitles} from '../api/system/admin/department'
import {useLazyQuery} from '@apollo/client'
import {GET_ALL_NOTIFICATIONS_GQL} from '../layoutv2/header/components/notify/notifyGql'
import _ from 'lodash'

const MainAppContext = createContext({})
export const MainAppContextProvider = ({ children }) => {
    const { page } = useSelector(
        (state) => state.layoutInteractionReducer
    )
    const [allUserInformation, setAllUserInformation] = useState([])
    const [allProductsService, setAllProductsService] = useState([])
    const [employeeHrm, setEmployeeHrm] = useState([])
    const [employeeReference, setEmployeeReference] = useState([])
    const [departmentData, setDepartmentData] = useState([])
    const [jobTitleData, setJobTitleData] = useState([])

    const [
        getAllNotifications,
        {
            data: allNotifications,
            refetch: refetchAllNotification
        }
    ] = useLazyQuery(GET_ALL_NOTIFICATIONS_GQL)
    const [notificationCount, setNotificationCount] = useState(0)

    const app = initializeApp(firebaseConfig)
    const messaging = getMessaging(app)

    const addAvatarToEmployeeHrmData = (hrmData) => {
        const allUserInformation = JSON.parse(
            localStorage.getItem('allUserInformation') || '[]'
        )

        if (!allUserInformation?.length) return hrmData
        return hrmData?.map((hrm) => {
            const user = allUserInformation?.find(
                (user) => user?.objectId === hrm?.id
            )
            return {
                ...hrm,
                avatar: user?.avatar
            }
        })
    }

    useEffect(() => {
        getAllUserInformation()
            .then((response) => {
                setAllUserInformation(response?.items)
                localStorage.setItem(
                    'allUserInformation',
                    JSON.stringify(response?.items)
                )
            })
            .catch(() => {})
    }, [])

    useEffect(() => {
        getAllProducts({ search: null, filter: null, sort: null })
            .then((response) => {
                setAllProductsService(response?.items)
                localStorage.setItem(
                    'allProductsService',
                    JSON.stringify(response?.items)
                )
            })
            .catch(() => {})
    }, [])

    useEffect(() => {
        loadEmployeeReferencesApi(null, 1, 1000, null, null).then(
            (response) => {
                setEmployeeReference(response?.items)
                const employeeHrm = response?.items?.filter(
                    (item) => item.type === 'employee'
                )
                setEmployeeHrm(addAvatarToEmployeeHrmData(employeeHrm))
            }
        )
    }, []);

    useEffect(() => {
        getDepartments(null, 1, 100, null, null, null)
            .then((response) => {
                setDepartmentData(response?.items)
            })
            .catch((error) => {
                console.log(error)
            })
    }, []);

    useEffect(() => {
        getJobTitles(null, 1, 100, null, null, null)
            .then((response) => {
                setJobTitleData(response?.items)
            })
            .catch((error) => {
                console.log(error)
            })
    }, []);

    useEffect(() => {
        getAllNotifications({
            variables: {
                category: notificationCategory,
                page: page,
                pageSize: 20,
                search: null,
                type: 'notification',
                status: null,
            }
        }).then((response) => {
            localStorage.setItem(
                'allNotificationData',
                JSON.stringify(response)
            )
        })
            .catch(() => {})
    }, [])

    useEffect(() => {
        getNotifyCount()
    }, []);

    const refetchUserInformation = useCallback(
        debounce(() => {
            getAllUserInformation()
                .then((response) => {
                    setAllUserInformation(response?.items)
                    localStorage.setItem(
                        'allUserInformation',
                        JSON.stringify(response?.items)
                    )
                })
                .catch(() => {})
        }, 500),
        []
    )

    const getNotifyCount = useCallback(() => {
        getCountNotificationByEmployeeId(
            notificationCategory,
            'notification',
            'noRead',
            null,
            false
        ).then((res) => {
            setNotificationCount(res)
        })
    }, []);

    const deboundRefetchAllNotification = _.debounce((page, status) => {
        refetchAllNotification({page, status}).then(r => r)
        getNotifyCount()
    }, 500)

    onMessage(messaging, (payload) => {
        if (payload.notification?.title && payload.notification?.body) {
            showNotification(
                payload.notification?.title,
                payload.notification?.body
            )
        }
        if (notificationCategory.includes(payload.data?.category)) {
            localStorage.removeItem('allNotificationData')
            deboundRefetchAllNotification(1, null)
        } else if (payload.data?.category === 'reloadUser') {
            setAllUserInformation([])
            refetchUserInformation()
        }
    })

    const showNotification = (title, body) => {
        const notificationTitle = title
        const notificationOptions = {
            body: body,
            icon: '/logo/service-circle/doTP.png'
        }

        // show notification using the Service Worker registration
        navigator.serviceWorker.ready.then((registration) => {
            registration.showNotification(notificationTitle, notificationOptions)
        })
    }

    const contextValue = useMemo(() => {
        return {
            allUserInformation,
            allProductsService,
            allNotifications: allNotifications?.notification,
            employeeHrm,
            employeeReference,
            departmentData,
            jobTitleData,
            refetchUserInformation,
            refetchAllNotification,
            notificationCount,
            getNotifyCount,
            setNotificationCount,
        }
    }, [
        allUserInformation,
        allProductsService,
        allNotifications,
        employeeHrm,
        employeeReference,
        jobTitleData,
        departmentData,
        refetchAllNotification,
        refetchUserInformation,
        notificationCount
    ])

    return (
        <MainAppContext.Provider value={contextValue}>
            {children}
        </MainAppContext.Provider>
    )
}

export const useMainAppContext = () => useContext(MainAppContext)
